<template>
  <div class="hc-compact-page patient-detail-page">
    <PageHeader
      title="Detail Pasien"
      :subtitle="data.id ? `Profil, kontak, dan informasi medis ${patientName}` : 'Memuat data pasien'"
      icon="fas fa-user-injured"
    >
      <template #actions>
        <div class="patient-detail-actions">
          <b-button variant="outline-secondary" @click="$router.push('/patient/list')">
            <i class="fas fa-arrow-left"></i><span>Kembali</span>
          </b-button>
          <b-button
            v-if="data.id"
            variant="outline-primary"
            @click="$router.push({ path: '/vertical-clinical/mcu_conclusion', query: { patient_id: data.id } })"
          >
            <i class="fas fa-clipboard-check"></i><span>Medical Check Up</span>
          </b-button>
          <b-button
            v-if="data.id && manipulateBtn"
            variant="primary"
            @click="$router.push({ path: '/patient/edit/' + data.id })"
          >
            <i class="far fa-edit"></i><span>Edit Pasien</span>
          </b-button>
        </div>
      </template>
    </PageHeader>

    <div v-if="loading" class="patient-detail-loading">
      <b-spinner small variant="primary" />
      <span>Memuat detail pasien...</span>
    </div>

    <template v-else-if="data.id">
      <section class="patient-profile-card">
        <div class="patient-profile-card__identity">
          <div class="patient-avatar">
            <img
              v-if="data.photo && !imageError"
              :src="data.photo"
              :alt="patientName"
              @load="onImageLoad"
              @error="imageError = true"
            />
            <span v-else>{{ patientInitials }}</span>
          </div>
          <div class="patient-profile-card__content">
            <div class="patient-profile-card__eyebrow">Profil Pasien</div>
            <h2>{{ patientName }}</h2>
            <div class="patient-profile-card__meta">
              <span><i class="far fa-id-card"></i>{{ displayValue(data.patient_id) }}</span>
              <span><i class="fas fa-venus-mars"></i>{{ displayValue(formattedGender) }}</span>
              <span><i class="fas fa-birthday-cake"></i>{{ birthSummary }}</span>
            </div>
            <div class="patient-profile-card__badges">
              <span class="patient-badge" :class="statusBadgeClass">
                <i class="fas fa-circle"></i>{{ displayValue(data.status, 'Status belum tersedia') }}
              </span>
              <span class="patient-badge patient-badge--coverage">
                <i :class="isBpjs ? 'fas fa-shield-alt' : 'fas fa-wallet'"></i>
                {{ isBpjs ? 'BPJS/JKN' : 'Pasien Umum' }}
              </span>
              <span v-if="data.blood_type" class="patient-badge patient-badge--blood">
                <i class="fas fa-tint"></i>Golongan {{ data.blood_type }}
              </span>
            </div>
          </div>
        </div>

        <div class="patient-profile-card__quick-contact">
          <div class="patient-quick-contact__icon"><i class="fas fa-phone-alt"></i></div>
          <div>
            <span>Kontak utama</span>
            <strong>{{ primaryPhone }}</strong>
            <small>{{ displayValue(data.email, 'Email belum tersedia') }}</small>
          </div>
        </div>
      </section>

      <div class="patient-detail-grid">
        <section class="patient-info-card">
          <div class="patient-info-card__header">
            <div class="patient-info-card__icon"><i class="far fa-address-card"></i></div>
            <div>
              <h3>Identitas Pribadi</h3>
              <p>Data dasar dan identitas resmi pasien</p>
            </div>
          </div>
          <div class="patient-info-list">
            <DetailRow icon="fas fa-fingerprint" label="Nomor KTP/NIK" :value="data.id_card_number" />
            <DetailRow icon="fas fa-map-marker-alt" label="Tempat lahir" :value="data.birt_place" />
            <DetailRow icon="far fa-calendar-alt" label="Tanggal lahir" :value="formattedBirthDate" />
            <DetailRow icon="fas fa-hourglass-half" label="Umur" :value="ageLabel" />
            <DetailRow icon="fas fa-venus-mars" label="Jenis kelamin" :value="formattedGender" />
            <DetailRow icon="fas fa-user-friends" label="Nama orang tua" :value="data.parent_name" />
          </div>
        </section>

        <section class="patient-info-card">
          <div class="patient-info-card__header">
            <div class="patient-info-card__icon patient-info-card__icon--contact"><i class="fas fa-address-book"></i></div>
            <div>
              <h3>Kontak &amp; Alamat</h3>
              <p>Informasi komunikasi dan domisili pasien</p>
            </div>
          </div>
          <div class="patient-info-list">
            <DetailRow icon="fas fa-mobile-alt" label="Telepon seluler" :value="data.mobile_phone" />
            <DetailRow icon="fas fa-phone" label="Telepon lainnya" :value="data.phone" />
            <DetailRow icon="far fa-envelope" label="Email" :value="data.email" />
            <DetailRow icon="fas fa-map-marked-alt" label="Domisili" :value="regionSummary" />
            <div class="patient-info-row patient-info-row--wide">
              <div class="patient-info-row__icon"><i class="fas fa-home"></i></div>
              <div class="patient-info-row__content">
                <span>Alamat lengkap</span>
                <strong>{{ fullAddress }}</strong>
              </div>
            </div>
          </div>
        </section>

        <section class="patient-info-card">
          <div class="patient-info-card__header">
            <div class="patient-info-card__icon patient-info-card__icon--medical"><i class="fas fa-notes-medical"></i></div>
            <div>
              <h3>Informasi Medis</h3>
              <p>Ringkasan data penting untuk pelayanan</p>
            </div>
          </div>
          <div class="patient-medical-summary">
            <div class="patient-medical-highlight patient-medical-highlight--blood">
              <i class="fas fa-tint"></i>
              <div><span>Golongan darah</span><strong>{{ displayValue(data.blood_type) }}</strong></div>
            </div>
            <div class="patient-medical-highlight patient-medical-highlight--allergy">
              <i class="fas fa-allergies"></i>
              <div><span>Riwayat alergi</span><strong>{{ displayValue(data.allergy, 'Tidak ada data alergi') }}</strong></div>
            </div>
          </div>
          <div v-if="isBpjs" class="patient-bpjs-box">
            <div class="patient-bpjs-box__title"><i class="fas fa-shield-alt"></i> Kepesertaan BPJS/JKN</div>
            <div class="patient-info-list">
              <DetailRow icon="far fa-credit-card" label="Nomor kartu" :value="data.bpjs_card_number" />
              <DetailRow icon="fas fa-check-circle" label="Status peserta" :value="data.bpjs_participant_status" />
              <DetailRow icon="fas fa-clinic-medical" label="FKTP" :value="data.bpjs_fktp_name" />
              <DetailRow icon="fas fa-hashtag" label="Kode FKTP" :value="data.bpjs_fktp_code" />
            </div>
          </div>
        </section>

        <section class="patient-info-card">
          <div class="patient-info-card__header">
            <div class="patient-info-card__icon patient-info-card__icon--doctor"><i class="fas fa-user-md"></i></div>
            <div>
              <h3>Tim Perawatan</h3>
              <p>Dokter yang menangani dan penanggung jawab pasien</p>
            </div>
          </div>
          <div class="patient-doctor-groups">
            <div class="patient-doctor-group">
              <span class="patient-doctor-group__label">Dokter yang menangani</span>
              <div v-if="listData.length" class="patient-doctor-list">
                <div v-for="doctor in listData" :key="`care-${doctor.id}`" class="patient-doctor-chip">
                  <i class="fas fa-user-md"></i>{{ doctor.name }}
                </div>
              </div>
              <div v-else class="patient-empty-value">Belum ada dokter yang ditetapkan.</div>
            </div>
            <div class="patient-doctor-group">
              <span class="patient-doctor-group__label">Dokter Penanggung Jawab (DPJP)</span>
              <div v-if="dpjb.length" class="patient-doctor-list">
                <div v-for="doctor in dpjb" :key="`dpjp-${doctor.id}`" class="patient-doctor-chip patient-doctor-chip--primary">
                  <i class="fas fa-stethoscope"></i>{{ doctor.name }}
                </div>
              </div>
              <div v-else class="patient-empty-value">Belum ada DPJP yang ditetapkan.</div>
            </div>
          </div>
        </section>
      </div>

      <section class="patient-sub-card">
        <div class="patient-sub-card__header">
          <div>
            <h3>Daftar Sub Pasien</h3>
            <p>Anggota keluarga atau pasien terkait dalam profil ini</p>
          </div>
          <b-button
            v-if="manipulateBtn"
            variant="outline-primary"
            @click="showModalSubPatient(data.id, patientName)"
          >
            <i class="fas fa-plus"></i> Tambah Sub Pasien
          </b-button>
        </div>
        <b-table
          v-if="subPatient.length"
          responsive
          hover
          class="patient-sub-table mb-0"
          :items="subPatient"
          :fields="fields"
        >
          <template #cell(actions)="row">
            <div class="patient-table-actions" v-if="manipulateBtn">
              <b-button size="sm" variant="outline-primary" title="Edit" @click="btnEditOnClick(row.item)">
                <i class="fas fa-edit"></i>
              </b-button>
              <b-button size="sm" variant="outline-danger" title="Hapus" @click="btnDeleteOnClick(row.item.id)">
                <i class="fas fa-trash"></i>
              </b-button>
            </div>
          </template>
        </b-table>
        <div v-else class="patient-sub-empty">
          <div><i class="fas fa-user-friends"></i></div>
          <strong>Belum ada sub pasien</strong>
          <span>Tambahkan anggota keluarga atau pasien terkait bila diperlukan.</span>
        </div>
      </section>

      <div v-if="manipulateBtn" class="patient-danger-zone">
        <div>
          <strong>Hapus data pasien</strong>
          <span>Data yang dihapus tidak akan tampil lagi pada daftar pasien.</span>
        </div>
        <b-button variant="outline-danger" @click="deleteData(data.id)">
          <i class="far fa-trash-alt"></i> Hapus Pasien
        </b-button>
      </div>
    </template>

    <ModalSubPatient
      :patientId="formSub.patient_id"
      :patientName="formSub.patient_name"
      :form="formSub"
    />
  </div>
</template>

<script>
import PageHeader from "@/component/general/PageHeader.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import module from "@/core/modules/CrudModule.js";
import moment from "moment";
import ModalSubPatient from "@/component/patients/ModalSubPatient.vue";

const DetailRow = {
  functional: true,
  props: {
    icon: String,
    label: String,
    value: [String, Number],
  },
  render(createElement, context) {
    const value = context.props.value;
    return createElement("div", { class: "patient-info-row" }, [
      createElement("div", { class: "patient-info-row__icon" }, [
        createElement("i", { class: context.props.icon }),
      ]),
      createElement("div", { class: "patient-info-row__content" }, [
        createElement("span", context.props.label),
        createElement("strong", value === null || value === undefined || value === "" ? "-" : String(value)),
      ]),
    ]);
  },
};

export default {
  components: {
    PageHeader,
    ModalSubPatient,
    DetailRow,
  },

  data() {
    return {
      data: {},
      subPatient: [],
      listData: [],
      dpjb: [],
      imgLoaded: false,
      imageError: false,
      loading: true,
      manipulateBtn: false,
      fields: [
        { key: "number", label: "#", sortable: true },
        { key: "name", label: "Nama" },
        { key: "born_date", label: "Tgl. Lahir" },
        { key: "notes", label: "Deskripsi" },
        { key: "actions", label: "", thClass: "patient-actions-column" },
      ],
      formSub: {
        endPoint: "",
        id: "",
        name: "",
        birt_date: "",
        notes: "",
        patient_id: "",
        patient_name: "",
      },
    };
  },

  computed: {
    patientName() {
      return this.data.patient_name || this.data.name || "Pasien";
    },
    patientInitials() {
      return this.patientName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
    formattedGender() {
      const gender = String(this.data.gender || "").toLowerCase();
      if (["male", "l", "laki-laki", "laki laki"].includes(gender)) return "Laki-laki";
      if (["female", "p", "perempuan"].includes(gender)) return "Perempuan";
      return this.data.gender || "-";
    },
    formattedBirthDate() {
      if (!this.data.birt_date) return "-";
      const date = moment(this.data.birt_date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
      return date.isValid() ? date.format("DD MMMM YYYY") : this.data.birt_date;
    },
    ageLabel() {
      return this.data.age === null || this.data.age === undefined ? "-" : `${this.data.age} tahun`;
    },
    birthSummary() {
      return this.formattedBirthDate === "-" ? this.ageLabel : `${this.formattedBirthDate} · ${this.ageLabel}`;
    },
    primaryPhone() {
      return this.data.mobile_phone || this.data.phone || "Nomor telepon belum tersedia";
    },
    fullAddress() {
      return [this.data.address, this.data.village_name, this.data.subdistrict_name, this.data.capital_name, this.data.province_name]
        .filter((value, index, items) => value && items.indexOf(value) === index)
        .join(", ") || "Alamat belum tersedia";
    },
    regionSummary() {
      return [this.data.capital_name, this.data.province_name].filter(Boolean).join(", ") || "-";
    },
    isBpjs() {
      return String(this.data.coverage_type || "").toLowerCase() === "bpjs" || Boolean(this.data.bpjs_card_number);
    },
    statusBadgeClass() {
      const status = String(this.data.status || "").toLowerCase();
      return status.includes("aktif") ? "patient-badge--success" : "patient-badge--neutral";
    },
  },

  methods: {
    displayValue(value, fallback = "-") {
      return value === null || value === undefined || value === "" ? fallback : value;
    },
    onImageLoad() {
      this.imgLoaded = true;
    },
    async get() {
      this.loading = true;
      const patient = await module.get("patients/" + this.$route.params.id);
      if (!patient) {
        this.$router.push("/patient/list");
        return;
      }

      this.data = patient;
      this.data.age = patient.birt_date ? moment().diff(moment(patient.birt_date), "years") : null;
      this.imageError = false;
      await this.getDetailPatient();
      this.loading = false;
    },
    async getSub() {
      this.resetSubPatientForm();
      const response = await module.get("sub-patients/" + this.$route.params.id);
      this.subPatient = Array.isArray(response) ? response : [];
    },
    resetSubPatientForm() {
      this.formSub.endPoint = "add-sub-patients";
      this.formSub.id = "";
      this.formSub.name = "";
      this.formSub.notes = "";
      this.formSub.birt_date = "";
      this.formSub.patient_id = "";
      this.formSub.patient_name = "";
      delete this.formSub._method;
    },
    showModalSubPatient(id, name) {
      this.resetSubPatientForm();
      this.formSub.patient_id = id;
      this.formSub.patient_name = name;
      this.$bvModal.show("add-sub");
    },
    btnEditOnClick(data) {
      const selected = this.subPatient.find((item) => item.id == data.id);
      if (!selected) return;
      this.formSub.endPoint = "update-sub-patients/" + selected.id;
      this.formSub.id = selected.id;
      this.formSub.name = selected.name;
      this.formSub.notes = selected.notes;
      this.formSub.birt_date = selected.born_date;
      this.formSub.patient_id = selected.patient_id;
      this.formSub.patient_name = selected.patient_name;
      this.formSub._method = "put";
      this.$bvModal.show("add-sub");
    },
    async btnDeleteOnClick(id) {
      const result = await module.delete("delete-sub-patients/" + id);
      if (result) this.getSub();
    },
    async deleteData(id) {
      const result = await module.delete("patients/" + id);
      if (result) this.$router.push("/patient/list");
    },
    async getDetailPatient() {
      const doctorIds = Array.isArray(this.data.doctor_in_charge) ? this.data.doctor_in_charge : [];
      const responsibleDoctorIds = Array.isArray(this.data.responsible_doctor) ? this.data.responsible_doctor : [];
      const [careDoctors, responsibleDoctors] = await Promise.all([
        Promise.all(doctorIds.map((id) => module.get("doctors/" + id))),
        Promise.all(responsibleDoctorIds.map((id) => module.get("doctors/" + id))),
      ]);
      this.listData = careDoctors.filter(Boolean);
      this.dpjb = responsibleDoctors.filter(Boolean);
    },
    async setActiveMenu() {
      let accessRights = [];
      try {
        accessRights = JSON.parse(window.localStorage.getItem("access_right_display") || "[]");
      } catch (error) {
        accessRights = [];
      }
      this.manipulateBtn = accessRights.includes("3002");
    },
  },

  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Pasien", route: "" },
      { title: "Daftar Pasien", route: "/patient/list" },
      { title: "Detail" },
    ]);
    this.$root.$on("updateSubPatient", this.getSub);
    this.get();
    this.getSub();
    this.setActiveMenu();
  },

  beforeDestroy() {
    this.$root.$off("updateSubPatient", this.getSub);
  },
};
</script>

<style lang="scss" scoped>
.patient-detail-page { color: #1e293b; }

.patient-detail-actions { display: flex; align-items: center; gap: 8px; }
.patient-detail-actions .btn {
  min-height: 40px; display: inline-flex; align-items: center; gap: 7px;
  padding: 0.55rem 1rem; border-radius: 10px; font-size: 0.82rem; font-weight: 700;
}
.patient-detail-actions i { margin: 0 !important; }

.patient-detail-loading {
  min-height: 320px; display: flex; align-items: center; justify-content: center; gap: 10px;
  border: 1px solid #e7edf4; border-radius: 16px; background: #fff; color: #64748b; font-weight: 600;
}

.patient-profile-card {
  display: flex; align-items: stretch; justify-content: space-between; gap: 24px;
  margin-bottom: 14px; padding: 24px; overflow: hidden; border: 1px solid #dceae8;
  border-radius: 18px;
  background: radial-gradient(circle at 8% 15%, rgba(30, 149, 143, 0.1), transparent 31%), linear-gradient(135deg, #fff 0%, #f6fbfb 100%);
  box-shadow: 0 12px 30px rgba(20, 50, 99, 0.06);
}
.patient-profile-card__identity { min-width: 0; display: flex; align-items: center; gap: 18px; }
.patient-avatar {
  width: 82px; height: 82px; flex: 0 0 82px; display: flex; align-items: center; justify-content: center;
  overflow: hidden; border: 5px solid rgba(255, 255, 255, 0.88); border-radius: 23px;
  background: linear-gradient(135deg, #1e958f, #14608f); box-shadow: 0 10px 24px rgba(30, 149, 143, 0.22);
  color: #fff; font-size: 1.35rem; font-weight: 800; letter-spacing: 0.04em;
}
.patient-avatar img { width: 100%; height: 100%; object-fit: cover; }
.patient-profile-card__content { min-width: 0; }
.patient-profile-card__eyebrow { margin-bottom: 3px; color: #1e958f; font-size: 0.7rem; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; }
.patient-profile-card h2 { margin: 0 0 8px; color: #172033; font-size: 1.35rem; font-weight: 750; line-height: 1.3; }
.patient-profile-card__meta, .patient-profile-card__badges { display: flex; flex-wrap: wrap; align-items: center; gap: 7px 14px; }
.patient-profile-card__meta { color: #64748b; font-size: 0.77rem; font-weight: 600; }
.patient-profile-card__meta span { display: inline-flex; align-items: center; gap: 6px; }
.patient-profile-card__meta i { color: #8aa0b5; }
.patient-profile-card__badges { margin-top: 12px; }
.patient-badge { display: inline-flex; align-items: center; gap: 7px; padding: 5px 10px; border-radius: 999px; font-size: 0.7rem; font-weight: 800; }
.patient-badge .fa-circle { font-size: 6px; }
.patient-badge--success { background: #e8f8f0; color: #14764b; }
.patient-badge--neutral { background: #f2f5f8; color: #64748b; }
.patient-badge--coverage { background: #edf7f6; color: #157b76; }
.patient-badge--blood { background: #fff0f0; color: #b64545; }
.patient-profile-card__quick-contact {
  min-width: 290px; display: flex; align-items: center; gap: 14px; padding: 17px 19px;
  border: 1px solid #dceae8; border-radius: 14px; background: rgba(255, 255, 255, 0.86);
}
.patient-quick-contact__icon { width: 46px; height: 46px; flex: 0 0 46px; display: flex; align-items: center; justify-content: center; border-radius: 12px; background: rgba(30, 149, 143, 0.1); color: #1e958f; }
.patient-profile-card__quick-contact span, .patient-profile-card__quick-contact strong, .patient-profile-card__quick-contact small { display: block; }
.patient-profile-card__quick-contact span { margin-bottom: 3px; color: #8494a6; font-size: 0.69rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
.patient-profile-card__quick-contact strong { color: #1e293b; font-size: 0.9rem; }
.patient-profile-card__quick-contact small { max-width: 230px; margin-top: 5px; overflow: hidden; color: #64748b; font-size: 0.75rem; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }

.patient-detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.patient-info-card, .patient-sub-card {
  padding: 20px; border: 1px solid #e4ebf2; border-radius: 16px; background: #fff;
  box-shadow: 0 8px 24px rgba(20, 50, 99, 0.045);
}
.patient-info-card__header { display: flex; align-items: center; gap: 12px; margin-bottom: 17px; padding-bottom: 14px; border-bottom: 1px solid #edf1f5; }
.patient-info-card__icon { width: 40px; height: 40px; flex: 0 0 40px; display: flex; align-items: center; justify-content: center; border-radius: 11px; background: #edf7f6; color: #1e958f; }
.patient-info-card__icon--contact { background: #eef4ff; color: #3c67ba; }
.patient-info-card__icon--medical { background: #fff0f0; color: #bd4d4d; }
.patient-info-card__icon--doctor { background: #f3efff; color: #7254b3; }
.patient-info-card__header h3, .patient-sub-card__header h3 { margin: 0 0 2px; color: #1e293b; font-size: 0.92rem; font-weight: 800; }
.patient-info-card__header p, .patient-sub-card__header p { margin: 0; color: #8a99aa; font-size: 0.72rem; }
.patient-info-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 15px 18px; }
::v-deep .patient-info-row { min-width: 0; display: flex; align-items: flex-start; gap: 10px; }
::v-deep .patient-info-row--wide { grid-column: 1 / -1; }
::v-deep .patient-info-row__icon { width: 30px; height: 30px; flex: 0 0 30px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: #f4f7fa; color: #71859a; font-size: 0.72rem; }
::v-deep .patient-info-row__content { min-width: 0; }
::v-deep .patient-info-row__content span, ::v-deep .patient-info-row__content strong { display: block; }
::v-deep .patient-info-row__content span { margin-bottom: 2px; color: #8a99aa; font-size: 0.68rem; font-weight: 650; }
::v-deep .patient-info-row__content strong { overflow-wrap: anywhere; color: #334155; font-size: 0.79rem; font-weight: 750; line-height: 1.45; }

.patient-medical-summary { display: grid; grid-template-columns: 0.7fr 1.3fr; gap: 12px; }
.patient-medical-highlight { display: flex; align-items: flex-start; gap: 11px; padding: 14px; border-radius: 12px; }
.patient-medical-highlight > i { width: 28px; padding-top: 2px; text-align: center; font-size: 1rem; }
.patient-medical-highlight span, .patient-medical-highlight strong { display: block; }
.patient-medical-highlight span { margin-bottom: 3px; font-size: 0.68rem; font-weight: 700; opacity: 0.72; }
.patient-medical-highlight strong { font-size: 0.8rem; line-height: 1.4; }
.patient-medical-highlight--blood { background: #fff1f1; color: #ad4141; }
.patient-medical-highlight--allergy { background: #fff8e8; color: #94630d; }
.patient-bpjs-box { margin-top: 14px; padding: 14px; border: 1px solid #d9eee5; border-radius: 12px; background: #f5fbf8; }
.patient-bpjs-box__title { margin-bottom: 13px; color: #17775a; font-size: 0.75rem; font-weight: 800; }
.patient-bpjs-box__title i { margin-right: 6px; }
.patient-doctor-groups { display: grid; gap: 17px; }
.patient-doctor-group__label { display: block; margin-bottom: 8px; color: #7c8da0; font-size: 0.7rem; font-weight: 800; text-transform: uppercase; }
.patient-doctor-list { display: flex; flex-wrap: wrap; gap: 8px; }
.patient-doctor-chip { display: inline-flex; align-items: center; gap: 7px; padding: 8px 11px; border-radius: 10px; background: #f2f6f9; color: #52667c; font-size: 0.75rem; font-weight: 750; }
.patient-doctor-chip--primary { background: #edf7f6; color: #157b76; }
.patient-empty-value { padding: 12px; border: 1px dashed #dce4eb; border-radius: 10px; color: #91a0af; font-size: 0.75rem; }

.patient-sub-card { margin-top: 14px; }
.patient-sub-card__header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.patient-sub-card__header .btn { border-radius: 9px; font-size: 0.75rem; font-weight: 750; }
::v-deep .patient-sub-table thead th { border-top: 0; border-bottom: 1px solid #e6edf3; background: #f7f9fb; color: #718096; font-size: 0.68rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
::v-deep .patient-sub-table td { vertical-align: middle; border-color: #edf1f5; color: #45566a; font-size: 0.78rem; }
.patient-table-actions { display: flex; justify-content: flex-end; gap: 6px; }
.patient-table-actions .btn { width: 32px; height: 32px; padding: 0; border-radius: 8px; }
.patient-table-actions i { margin: 0 !important; }
.patient-sub-empty { min-height: 155px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 22px; text-align: center; }
.patient-sub-empty > div { width: 46px; height: 46px; display: flex; align-items: center; justify-content: center; margin-bottom: 10px; border-radius: 13px; background: #edf7f6; color: #1e958f; }
.patient-sub-empty strong { color: #445368; font-size: 0.82rem; }
.patient-sub-empty span { margin-top: 3px; color: #91a0af; font-size: 0.72rem; }

.patient-danger-zone { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-top: 14px; padding: 14px 18px; border: 1px solid #f2dada; border-radius: 14px; background: #fffafa; }
.patient-danger-zone strong, .patient-danger-zone span { display: block; }
.patient-danger-zone strong { margin-bottom: 2px; color: #8e3333; font-size: 0.78rem; }
.patient-danger-zone span { color: #9b6c6c; font-size: 0.7rem; }
.patient-danger-zone .btn { border-radius: 9px; font-size: 0.75rem; font-weight: 750; white-space: nowrap; }

@media (max-width: 991.98px) {
  .patient-profile-card { align-items: flex-start; flex-direction: column; }
  .patient-profile-card__quick-contact { width: 100%; min-width: 0; }
  .patient-detail-grid { grid-template-columns: 1fr; }
}

@media (max-width: 767.98px) {
  .patient-detail-actions { width: 100%; flex-wrap: wrap; }
  .patient-detail-actions .btn { flex: 1 1 auto; justify-content: center; }
  .patient-profile-card { padding: 18px; }
  .patient-profile-card__identity { align-items: flex-start; }
  .patient-avatar { width: 60px; height: 60px; flex-basis: 60px; border-radius: 17px; font-size: 1rem; }
  .patient-profile-card h2 { font-size: 1.08rem; }
  .patient-info-list, .patient-medical-summary { grid-template-columns: 1fr; }
  .patient-sub-card__header, .patient-danger-zone { align-items: stretch; flex-direction: column; }
  .patient-sub-card__header .btn { width: 100%; }
}
</style>
